import React, { useEffect, useState } from 'react';
import { Mail, Loader2, X, Sparkles } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';
import { PlatformPicker, PLATFORMS } from './PlatformPicker';

const DISMISS_KEY = 'tmj_popup_dismissed_at';
// Suppress the popup for 7 days after a user closes it. Prevents nagging
// return visitors while still catching newcomers a week later.
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
// Delay before the timed popup shows on any page load.
const APPEAR_DELAY_MS = 60 * 1000;
// Query param used by the shareable subscribe link — presence forces an
// immediate popup on landing so shared links convert without the 60s wait.
const FORCE_QUERY_PARAM = 'subscribe';

/**
 * SubscribePopup — shows after `APPEAR_DELAY_MS` on any page (or
 * immediately if the URL has `?subscribe=1`). Suppressed for subscribed
 * users and for 7 days after any explicit dismissal.
 *
 * Kept separate from `SignInPromptModal` because that modal is a
 * blocking gate for feature actions (watchlist/notify/share) whereas
 * this popup is a passive, time-triggered nudge — different context
 * copy and different suppression rules.
 */
const AlreadySubscribedNote = ({ email, onClose }) => (
  <div data-testid="subscribe-popup-already-subscribed">
    <p className="font-secondary text-sm text-gray-400 mb-6 leading-relaxed">
      <span className="text-white font-semibold break-all" data-testid="subscribe-popup-subscribed-email">{email}</span>{' '}
      is already subscribed. You'll keep getting the weekly drop-list, new-release alerts, and reminders a day before and the day of release.
    </p>
    <button
      type="button"
      onClick={onClose}
      data-testid="subscribe-popup-browse"
      className="w-full px-6 py-3 bg-[#5DADE2] hover:bg-[#85C1E9] text-black font-secondary font-bold rounded-full transition-colors"
    >
      Browse the guide
    </button>
  </div>
);

const SubscribePopup = () => {
  const { isSubscribed, email: subscribedEmail, subscribe } = useSubscription();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [platforms, setPlatforms] = useState(PLATFORMS);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [sharedContext, setSharedContext] = useState(false);

  useEffect(() => {
    // Force-open when landing via a shareable subscribe link. We ONLY
    // clear the dismissal cache when the query param is present, so a
    // shared link always wins over the 7-day suppression.
    const params = new URLSearchParams(window.location.search);
    const forced = params.has(FORCE_QUERY_PARAM);
    if (forced) {
      setSharedContext(true);
      setVisible(true);
      return;
    }
    if (isSubscribed) return;

    // Suppress if user closed the popup recently.
    try {
      const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;
    } catch { /* ignore storage failures */ }

    const timer = setTimeout(() => setVisible(true), APPEAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isSubscribed]);

  const handleClose = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* ignore */ }
    // Strip the ?subscribe param so a refresh doesn't re-open the popup
    // after the user has explicitly dismissed it.
    if (sharedContext) {
      const url = new URL(window.location.href);
      url.searchParams.delete(FORCE_QUERY_PARAM);
      window.history.replaceState({}, '', url.toString());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setError('');
    try {
      await subscribe(email, platforms);
      setStatus('success');
      // Auto-close after the success flash, and don't count this as a
      // "dismiss" — subscribed users won't ever see the popup again.
      setTimeout(() => setVisible(false), 1400);
    } catch (err) {
      setStatus('error');
      setError(err?.response?.data?.detail || 'Subscription failed. Please try again.');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const alreadySubscribed = isSubscribed && status !== 'success';
  if (!visible || (alreadySubscribed && !sharedContext)) return null;

  const heroTitle = alreadySubscribed
    ? "You're already on the list"
    : sharedContext
      ? 'You were invited to The Movie Junkie Streaming Guide'
      : 'Never miss a great release';
  const heroBlurb = sharedContext
    ? "A friend shared this streaming guide with you. Join the list and we'll email you the best drops every week, notify you as soon as something new drops, and send reminders a day before and the day of release."
    : "The best OTT + theatrical drops every week, plus an alert as soon as something new drops and reminders a day before and the day of release.";

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={handleClose}
      data-testid="subscribe-popup-overlay"
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          data-testid="subscribe-popup-close"
          aria-label="Close"
          className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors z-10"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        <div className="p-8 text-center">
          <div className="inline-flex p-4 bg-[#5DADE2]/20 rounded-full mb-4">
            <Sparkles className="w-8 h-8 text-[#5DADE2]" />
          </div>

          <h2
            className="font-primary text-2xl uppercase tracking-wide text-white mb-3"
            data-testid="subscribe-popup-title"
          >
            {heroTitle}
          </h2>

          {alreadySubscribed ? (
            <AlreadySubscribedNote email={subscribedEmail} onClose={handleClose} />
          ) : (
          <>
          <p className="font-secondary text-sm text-gray-400 mb-6 leading-relaxed" data-testid="subscribe-popup-blurb">
            {heroBlurb}
          </p>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
                disabled={status === 'loading' || status === 'success'}
                data-testid="subscribe-popup-email"
                className="w-full pl-12 pr-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-500 font-secondary focus:outline-none focus:border-[#5DADE2]/60 transition-all disabled:opacity-50"
              />
            </div>

            <PlatformPicker
              selected={platforms}
              onChange={setPlatforms}
              disabled={status === 'loading' || status === 'success'}
            />

            <button
              type="submit"
              disabled={!email || platforms.length === 0 || status === 'loading' || status === 'success'}
              data-testid="subscribe-popup-submit"
              className="w-full px-6 py-3 bg-[#5DADE2] hover:bg-[#85C1E9] text-black font-secondary font-bold rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {status === 'loading' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Subscribing…</span>
                </>
              ) : status === 'success' ? (
                <span>Welcome aboard!</span>
              ) : (
                <span>Get the drop-list</span>
              )}
            </button>
          </form>

          {error && (
            <p data-testid="subscribe-popup-error" className="text-red-400 text-sm mt-3 font-secondary">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={handleClose}
            data-testid="subscribe-popup-maybe-later"
            className="mt-4 text-xs text-gray-500 hover:text-gray-300 font-secondary underline underline-offset-2"
          >
            Maybe later
          </button>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscribePopup;
